import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Typography } from '@mui/joy';
import { Stepper, Step, StepLabel, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Text, Input, Button } from 'src/mtska-frontend-app-component';
import Rules from './Rules';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import List from './ListLead';
import dayjs from 'dayjs';
import { useDataProvider } from "../../../mtska-frontend-data-provider";

const AudienceSegmentCreator = ({
    externalSetForwardDisabled,
    externalSetBackDisabled,
    externalSetActionInvoked,
    externalSetActionLabel,
    externalSetErrorMessage,
    actionInvoked,
    onSave,
    isCompleted
}) => {
    const { t } = useTranslation();
    const steps = [t('Info'), t('Rules'), t('Summary'), t('Confirm')];
    const [activeStep, setActiveStep] = useState(0);
    const [mappingValue, setMappingValue] = useState([]);
    const [totalLeads, setTotalLeads] = useState(0);
    const isSaving = useRef(false);
    const [filters, setFilters] = useState({ items: [] });

    const { control, setValue, watch, formState: { errors } } = useForm({
        mode: 'onChange',
        defaultValues: {
            segmentName: '',
            description: '',
            segmentColor: '#E9B712',
            creationDate: dayjs().format('YYYY-MM-DD')
        }
    });

    const { item: config } = useDataProvider('configs/config');
    const moduleConfig = config?.data?.modules?.['audiences/audiencelead'];
    const importModel = moduleConfig?.importModel || [];
    const modelFields = importModel.map(item => ({
        key: item.key,
        label: t(item.label)
    }));

    const [validationState, setValidationState] = useState({});

    useEffect(() => {
        externalSetBackDisabled(activeStep === 0 || isCompleted);
        externalSetForwardDisabled(isCompleted);
        externalSetActionLabel(activeStep === steps.length - 2 ? t('Submit') : (isCompleted ? t('Close') : t('Next')));
    }, [activeStep, steps.length, t, externalSetBackDisabled, externalSetForwardDisabled, externalSetActionLabel, isCompleted]);

    useEffect(() => {
        if (typeof actionInvoked === 'undefined' || actionInvoked === 0 || isCompleted) {
            return;
        }

        const currentStep = activeStep;
        const totalSteps = steps.length;

        if (actionInvoked > 0) {
            // Validazione in linea per evitare dipendenze instabili
            let stepError = null;
            if (currentStep === 0) {
                const segmentNameValue = watch('segmentName');
                if (!segmentNameValue || segmentNameValue.trim() === '') {
                    stepError = t('MandatoryName');
                }
            }

            if (stepError) {
                setValidationState(prev => ({ ...prev, [currentStep]: stepError }));
                externalSetErrorMessage(stepError);
                externalSetActionInvoked(0);
                return;
            } else {
                setValidationState(prev => ({ ...prev, [currentStep]: null }));
                externalSetErrorMessage(null);
            }

            if (currentStep === totalSteps - 2) {
                if (isSaving.current) return;

                isSaving.current = true;
                const formData = {
                    name: watch('segmentName'),
                    description: watch('description'),
                    color: watch('segmentColor'),
                    creation_date: watch('creationDate'),
                    rules: mappingValue,
                    status: 'draft',
                }
                if (onSave) {
                    onSave(formData)
                        .then(() => {
                            setActiveStep(currentStep + 1);
                            externalSetActionInvoked(0);
                            isSaving.current = false;
                        })
                        .catch(error => {
                            externalSetErrorMessage(error.message || t('failedSavingSegment'));
                            externalSetActionInvoked(0);
                            isSaving.current = false;
                        });
                } else {
                    isSaving.current = false;
                    setActiveStep(currentStep + 1);
                    externalSetActionInvoked(0);
                }
            } else {
                setActiveStep(currentStep + 1);
                externalSetActionInvoked(0);
            }
        } else if (actionInvoked < 0) {
            if (currentStep > 0) {
                setActiveStep(currentStep - 1);
                externalSetErrorMessage(null);
            }
            externalSetActionInvoked(0);
        }
    }, [actionInvoked, activeStep, steps.length, externalSetActionInvoked, externalSetErrorMessage, watch, mappingValue, onSave, t, isCompleted]);

    const getRuleValue = (key) => {
        const rule = mappingValue.find(r => r.key === key);
        if (!rule) return 'N/A';

        switch (key) {
            case 'citta':
                return rule.value.join(', ');
            case 'usage_kw':
                return `${rule.operator} ${rule.value} KWh`;
            default:
                return 'N/A';
        }
    };

    const summaryFields = [
        { label: 'Nome segmento', value: watch('segmentName') || 'N/A' },
        { label: 'Descrizione', value: watch('description') || 'N/A' },
        { label: 'Colore', value: watch('segmentColor') || 'N/A' },
        { label: 'Data creazione', value: watch('creationDate') || 'N/A' },
        { label: 'Totale lead a oggi', value: totalLeads },
    ];

    const handleTotalChange = useCallback((newTotal) => {
        setTotalLeads(newTotal);
    }, []);

    const handleRulesChange = useCallback((newRules) => {
        setMappingValue(newRules);
        setFilters({
            items: newRules.map(rule => ({
                field: rule.field,
                operator: rule.operator,
                value: rule.value
            }))
        });
    }, []);

    return (
        <Box className="audiencesegmentcreator">
            <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
                {steps.map((label, index) => {
                    const labelProps = {};
                    const stepError = validationState[index];
                    if (stepError) {
                        labelProps.optional = (
                            <Typography variant="caption" color="error">
                                {stepError}
                            </Typography>
                        );
                        labelProps.error = true;
                    }
                    return (
                        <Step key={label}>
                            <StepLabel {...labelProps}>{t(label)}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
            <Box className="form-wrapper">
                <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }} className="form-section-fields-wrapper">
                    <fieldset className="mtska-fieldset form-section-fields-content" data-type="fieldset" data-name="base_data" style={{ border: 'none' }}>
                        <Text level="title-md" className="mtska-fieldset-title form-element fullwidth">{t('infoSegment')}</Text>
                        <Box className="form-element text col-12">
                            <Input
                                label={t('segmentName')}
                                type="text"
                                name="segmentName"
                                control={control}
                                editing={true}
                                required={true}
                            />
                        </Box>
                        <Box className="form-element text col-12">
                            <Input
                                label={t('segmentDescription')}
                                type="textarea"
                                name="description"
                                control={control}
                                editing={true}
                                rows={3}
                            />
                        </Box>
                        <Box className="form-element text col-6">
                            <Input
                                label={t('segmentColor')}
                                type="colorpicker"
                                name="segmentColor"
                                control={control}
                                editing={true}
                            />
                        </Box>
                        <Box className="form-element text col-6">
                            <Input
                                label={t('creationDate')}
                                type="date"
                                name="creationDate"
                                control={control}
                                editing={false}
                            />
                        </Box>
                    </fieldset>
                </Box>
                <Box sx={{ display: activeStep === 1 ? 'block' : 'none', px: 2 }}>
                    <div className="form-element col-12" style={{ marginBottom: '32px' }}>
                        <Rules
                            model={modelFields}
                            control={control}
                            defaultRules={mappingValue}
                            onRulesChange={handleRulesChange}
                            setValue={setValue}
                        />
                    </div>

                    <List onTotalChange={handleTotalChange} filters={filters} />
                </Box>
                {/*                 <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
                    <fieldset className="mtska-fieldset form-section-fields-content" style={{ border: 'none' }}>
                        <Text level="title-md" className="mtska-fieldset-title form-element fullwidth" style={{ marginBottom: '32px' }} >{t('Reviews filtered data')}</Text>
                        <List onTotalChange={handleTotalChange} />
                    </fieldset>
                </Box> */}
                <Box sx={{ display: activeStep === 2 ? 'block' : 'none', px: 2, position: 'relative' }}>
                    <Box sx={{ p: 3, opacity: isSaving.current ? 0.5 : 1 }}>
                        <Typography level="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                            {t('segmentSummary')}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
                            {t('segmentWarning')}
                        </Typography>
                        <Typography level="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                            {t('Info')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {summaryFields.map((field, index) => (
                                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #eee' }}>
                                    <Typography level="body-md" sx={{ fontWeight: 'bold' }}>{field.label}</Typography>
                                    <Typography level="body-md">{field.value}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Typography level="body-md" sx={{ fontWeight: 'bold', mt: 3 }}>{t('Rules')}</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            {mappingValue.length > 0 ? (
                                mappingValue.map((rule, index) => {
                                    const fieldLabel = modelFields.find(f => f.key === rule.field)?.label || rule.field
                                    const valueText = Array.isArray(rule.value)
                                        ? rule.value.join(', ').toLowerCase()
                                        : String(rule.value).toLowerCase()
                                    return (
                                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #eee' }}>
                                            <Typography level="body-md" sx={{ fontWeight: 'bold' }}>
                                                {fieldLabel}
                                            </Typography>
                                            <Typography level="body-md">
                                                {rule.operator} {valueText}
                                            </Typography>
                                        </Box>
                                    )
                                })
                            ) : (
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                    {t('noRulesDefined')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {isSaving.current && (
                        <Box className="overlay-progress">
                            <CircularProgress className="circular-progress" />
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: isCompleted ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 4,
                            textAlign: 'center',
                            mx: 'auto'
                        }}
                    >
                        <Box>
                            <ThumbUpOutlinedIcon sx={{ fontSize: 40, color: '#2196f3' }} />
                        </Box>
                        <Typography level="h4" sx={{ color: '#1976d2', fontWeight: 600, mb: 1 }}>
                            {t('segmentSuccesfulCreation')}
                        </Typography>
                        <Typography level="body-lg" sx={{ color: '#546e7a' }}>
                            {t('segmentSucessfulSubtitle')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default AudienceSegmentCreator;